import React, { useContext, useMemo } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Card, Col, Row, Tag } from 'antd';
import dayjs from 'dayjs';

import ProfitabilityContext from '@totem/components/ticketing/details/profitability/profitabilityContext';
import TasksTable from '@totem/components/ticketing/details/profitability/tasksTable';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { ticketStyles } from '@totem/components/ticketing/details/ticketStyles';
import Currency from '@totem/components/ui_components/widgets/currency/Currency';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { formatDateOnly, isNotNull, isNull } from '@totem/utilities/common';

const styles = {
  totalLine: {
    borderTop: '3px double black',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  valueLine: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  headerLine: {
    borderBottom: '1px solid gray',
    display: 'flex',
    justifyContent: 'flex-end',
    fontWeight: 'bold',
  },
};

const ProfitabilityLayout = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { data } = useContext(ProfitabilityContext);
  const { ticket } = useContext(TicketContext);

  const showProfitability = useMemo<boolean>(() => {
    return (
      permissions.internal.hasPermissions(userProfile) &&
      isNotNull(data) &&
      isNotNull(data.ticket) &&
      data.ticket.project !== null &&
      (data.resources.length > 0 ||
        data.ticket.project.budgetedHours > 0 ||
        data.ticket.project.fee > 0)
    );
  }, [data]);

  const budgetedHours = useMemo(() => {
    if (isNotNull(ticket) && isNotNull(ticket.ticket.tasks)) {
      const budgeted = ticket.ticket.tasks.reduce((accumulator, record) => {
        return accumulator + record.budgetedHours;
      }, 0);
      return budgeted;
    }
    return 0;
  }, [ticket.ticket.tasks]);

  const completionDate = useMemo(() => {
    if (isNull(ticket) || isNull(ticket.ticket.project)) {
      return '-';
    }

    if (formatDateOnly(ticket.ticket.closedAt) !== '-') {
      return formatDateOnly(ticket.ticket.closedAt);
    }
    if (formatDateOnly(ticket.ticket.project.endDate) !== '') {
      const endDate = dayjs(ticket.ticket.project.endDate);
      const today = dayjs();
      if (endDate.isBefore(today)) {
        return (
          <Tag
            color={'warning'}
            variant={'solid'}
            icon={<ExclamationCircleOutlined />}
          >
            Past Target
          </Tag>
        );
      }
      if (endDate.isAfter(today)) {
        return (
          <span style={{ color: ticketStyles.colors.positive }}>
            {formatDateOnly(ticket.ticket.project.endDate)}
          </span>
        );
      }
    }

    return '-';
  }, [ticket.ticket]);

  if (!showProfitability) {
    return <span />;
  }

  const projectFee = isNotNull(data.ticket.project)
    ? data.ticket.project.fee
    : 0;
  const projectCost = data.laborCost + data.totalExpenses;
  const projectBillings =
    data.billableLaborAmount + data.ticket.project.fee + data.billableExpenses;

  const gutterSize = 16;

  return (
    <>
      <Row gutter={32}>
        <Col span={12}>
          <Card title={'Profitability'}>
            <Row gutter={gutterSize}>
              <Col span={6}>
                <b>Recap</b>
              </Col>
              <Col span={6} style={styles.valueLine}>
                <b>Target</b>
              </Col>
              <Col span={6} style={styles.valueLine}>
                <b>Actual</b>
              </Col>
              <Col span={6}>
                <span />
              </Col>
            </Row>
            <Row gutter={gutterSize}>
              <Col span={6}>Budgeted Hours</Col>
              <Col span={6} style={styles.valueLine}>
                <Hours amount={budgetedHours} />
              </Col>
              <Col span={6} style={styles.valueLine}>
                <Hours amount={data.actualHours} />
              </Col>
              <Col span={6}>
                <span />
              </Col>
            </Row>
            <Row gutter={gutterSize}>
              <Col span={6}>Completion Date</Col>
              <Col span={6} style={styles.valueLine}>
                {formatDateOnly(data.ticket.project.endDate)}
              </Col>
              <Col span={6} style={styles.valueLine}>
                {completionDate}
              </Col>
              <Col span={6}>
                <span />
              </Col>
            </Row>

            <br />
            <br />

            <Row gutter={gutterSize}>
              <Col span={6}>
                <span />
              </Col>
              <Col span={6} style={styles.headerLine}>
                Billable
              </Col>
              <Col span={6} style={styles.headerLine}>
                Cost
              </Col>
              <Col span={6} style={styles.headerLine}>
                Net
              </Col>
            </Row>
            <Row gutter={gutterSize}>
              <Col span={6}>Labor</Col>
              <Col span={6} style={styles.valueLine}>
                <Currency currency={'USD'} amount={data.billableLaborAmount} />
              </Col>
              <Col span={6} style={styles.valueLine}>
                <Currency currency={'USD'} amount={data.laborCost} />
              </Col>
              <Col span={6} style={styles.valueLine}>
                <Currency
                  currency={'USD'}
                  amount={data.billableLaborAmount - data.laborCost}
                />
              </Col>
            </Row>
            <Row gutter={gutterSize}>
              <Col span={6}>Expenses</Col>
              <Col span={6} style={styles.valueLine}>
                <Currency currency={'USD'} amount={data.billableExpenses} />
              </Col>
              <Col span={6} style={styles.valueLine}>
                <Currency currency={'USD'} amount={data.totalExpenses} />
              </Col>
              <Col span={6} style={styles.valueLine}>
                <Currency
                  currency={'USD'}
                  amount={data.billableExpenses - data.totalExpenses}
                />
              </Col>
            </Row>
            <Row gutter={gutterSize}>
              <Col span={6}>Project Fee</Col>
              <Col span={6} style={styles.valueLine}>
                <Currency currency={'USD'} amount={projectFee} />
              </Col>
              <Col span={6} style={styles.valueLine}>
                <span />
              </Col>
              <Col span={6} style={styles.valueLine}>
                <Currency currency={'USD'} amount={projectFee} />
              </Col>
            </Row>

            <Row gutter={gutterSize}>
              <Col span={6}>Totals</Col>
              <Col span={6} style={styles.totalLine}>
                <Currency currency={'USD'} amount={projectBillings} />
              </Col>
              <Col span={6} style={styles.totalLine}>
                <Currency currency={'USD'} amount={projectCost} />
              </Col>
              <Col span={6} style={styles.totalLine}>
                <Currency
                  currency={'USD'}
                  amount={projectBillings - projectCost}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col span={12}>
          <Card title={'Task Variance'}>
            <TasksTable />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ProfitabilityLayout;
